import { Fragment } from "react";
import { AlertTriangle } from "lucide-react";
import { cutLabel } from "@/lib/bom";

const pct = (v, s) => `${(v / s.bar) * 100}%`;

const BarViz = ({ p, s }) => (
  <div className="h-7 flex rounded-sm overflow-hidden bg-slate-800 border border-slate-600" data-testid="cut-bar-viz">
    <div style={{ width: pct(s.trim, s) }} className="bg-slate-600 shrink-0" title={`Trim ${s.trim} mm`} />
    {p.cuts.map((c, j) => (
      <Fragment key={j}>
        <div style={{ width: pct(c.len, s) }} title={cutLabel(c)}
          className={`shrink-0 ${j % 2 ? "bg-cyan-500/30" : "bg-cyan-500/20"} border-x border-cyan-400/50 text-[10px] font-mono text-cyan-50 flex items-center justify-center overflow-hidden whitespace-nowrap px-0.5`}>
          {c.len}<span className="text-cyan-300/70 ml-1 hidden md:inline">{c.mark}·{c.tag}</span>
        </div>
        {s.kerf > 0 && <div style={{ width: pct(s.kerf, s) }} className="bg-rose-500/80 shrink-0" />}
      </Fragment>
    ))}
    <div className="flex-1 min-w-0 bg-[repeating-linear-gradient(45deg,#334155_0,#334155_4px,#1e293b_4px,#1e293b_8px)]" title={`Offcut ${p.offcut} mm`} />
  </div>
);

const Group = ({ g, s, i }) => (
  <div className="rounded-lg border border-slate-800 p-3 space-y-2" data-testid={`cut-group-${i}`}>
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      {g.tag && <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300" data-testid="cut-group-tag">{g.tag}</span>}
      <span className="font-semibold text-sm text-slate-100">{g.name}{g.code ? ` (${g.code})` : ""}</span>
      <span className="text-xs text-slate-500 truncate">{g.system}</span>
      <span className="ml-auto font-mono text-[11px] text-slate-400" data-testid="cut-group-summary">{g.pieces} pcs · {g.bars} bar{g.bars !== 1 ? "s" : ""} · waste {g.waste}%</span>
    </div>
    {g.patterns.map((p, k) => (
      <div key={k} className="flex items-center gap-3">
        <span className="w-16 shrink-0 font-mono text-[11px] text-slate-400">P{k + 1} × {p.n}</span>
        <div className="flex-1 min-w-0"><BarViz p={p} s={s} /></div>
        <span className="w-32 shrink-0 font-mono text-[11px] text-slate-400 text-right">offcut <span className="text-slate-200">{p.offcut}</span> mm</span>
      </div>
    ))}
    {g.oversize.map((c, k) => (
      <p key={k} className="text-xs text-amber-300 flex items-center gap-1.5" data-testid="cut-oversize"><AlertTriangle size={12} />{c.len} mm {c.mark}·{c.tag} is longer than the usable bar. Order cut-to-size or plan a joint.</p>
    ))}
  </div>
);

export const CutPlanTab = ({ plan, s }) => (
  <div className="space-y-3" data-testid="cut-plan">
    <p className="text-xs text-slate-400">
      Best-fit decreasing optimisation · usable length {s.bar - 2 * s.trim} mm per bar ({s.trim} mm trimmed at each end) · {s.kerf} mm per saw cut ·
      {s.mode === "project" ? " offcuts shared across all items of the same profile." : " each item is cut from its own bars."}
    </p>
    {plan.groups.map((g, i) => <Group key={g.key} g={g} s={s} i={i} />)}
  </div>
);
